/**
 * Region-aware consent defaults — GA4 Path B.1 (#1641).
 *
 * GDPR (EU/EEA + UK) and an abundance-of-caution stance for Canada require
 * OPT-IN: analytics_storage defaults to denied and the consent banner is shown.
 * The US and other non-EU/CA regions follow an opt-OUT model (CCPA et al.) where
 * defaulting analytics_storage to *granted* is lawful — so US paid traffic
 * attributes without an Accept click (the biggest lever for clean paid-channel data).
 *
 * Advertising signals (ad_storage / ad_user_data / ad_personalization):
 *   • OPT-OUT regions (US/other, CCPA/CPRA) default them to GRANTED — the lawful
 *     opt-out model — so Google Ads remarketing / Meta / TikTok pixels can build
 *     audiences without an Accept click. A persistent "Your Privacy Choices"
 *     control (footer) lets these visitors opt out; that writes 'ads_optout'.
 *   • OPT-IN regions (EU/EEA/UK/CH + CA + unknown) keep advertising DENIED until
 *     an explicit Accept.
 * Session-recording consumers (Clarity, PostHog full capture) are NOT covered by
 * this ad default — they remain gated on an explicit Accept (readConsent()).
 *
 * Region is derived from the IP country stamped into the `bizrnr_geo` cookie by
 * middleware (`x-vercel-ip-country`), with a client-side timezone fallback when the
 * cookie is absent. Detection is best-effort; an unknown region defaults to DENIED.
 *
 * Per-country tuning beyond EU/CA (e.g. Brazil LGPD) is deferred to the Sprint 3+
 * compliance review noted on #1641.
 */

import type { ConsentState } from '@/lib/consent/read';

/** Cookie set by middleware with the visitor's IP country (ISO-3166 alpha-2). */
export const GEO_COOKIE = 'bizrnr_geo';

export type Region = 'eu' | 'ca' | 'us' | 'other' | 'unknown';

/**
 * Stored consent values written to `bizrnr_consent`:
 *   'granted'       — explicit full accept (analytics + ads)
 *   'denied'        — explicit reject (nothing)
 *   'analytics'     — LEGACY region-implied analytics-only grant (ads denied).
 *                     Treated as an upgradeable implied default (see below).
 *   'analytics_ads' — region-implied analytics + ads grant for OPT-OUT regions
 *                     (US/other, CCPA/CPRA). The current opt-out default.
 *   'ads_optout'    — explicit ad opt-out via the "Your Privacy Choices" control
 *                     (analytics kept, advertising denied). Durable; never upgraded.
 *   null            — no choice yet
 *
 * EXPLICIT vs IMPLIED: 'granted' / 'denied' / 'ads_optout' are explicit user
 * choices and are NEVER overwritten by a region default. 'analytics' and
 * 'analytics_ads' are implied region defaults; an existing 'analytics' cookie may
 * be UPGRADED to 'analytics_ads' when the visitor's current region is opt-out, so
 * the opt-out ad default reaches visitors tagged analytics-only before it existed.
 */
export type StoredConsent =
  | 'granted'
  | 'denied'
  | 'analytics'
  | 'analytics_ads'
  | 'ads_optout'
  | null;

/** EU-27 + EEA (IS/LI/NO) + UK + Switzerland — treated as GDPR-style opt-in. */
export const EU_EEA_UK_CH: ReadonlySet<string> = new Set([
  // EU-27
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU',
  'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  // EEA
  'IS', 'LI', 'NO',
  // UK + Switzerland (GDPR-equivalent regimes)
  'GB', 'CH',
]);

/** Classify an ISO country code (case-insensitive) into a consent region. */
export function regionForCountry(country: string | null | undefined): Region {
  if (!country) return 'unknown';
  const cc = country.trim().toUpperCase();
  if (!cc) return 'unknown';
  if (EU_EEA_UK_CH.has(cc)) return 'eu';
  if (cc === 'CA') return 'ca';
  if (cc === 'US') return 'us';
  return 'other';
}

/**
 * analytics_storage default for a region. Granted (opt-out) for US + other known
 * non-EU/CA countries; denied (opt-in) for EU/CA and any unknown region.
 */
export function analyticsGrantedByDefault(region: Region): boolean {
  return region === 'us' || region === 'other';
}

/**
 * ad_storage default for a region. Granted (opt-out, CCPA/CPRA) for US + other
 * known non-EU/CA countries; denied (opt-in) for EU/CA and any unknown region.
 * Mirrors {@link analyticsGrantedByDefault}: opt-out regions grant both signals.
 */
export function adsGrantedByDefault(region: Region): boolean {
  return analyticsGrantedByDefault(region);
}

// IANA timezones that confidently indicate Canada (opt-in) — used by the client
// fallback so a Canadian visitor without a geo cookie is never auto-granted.
const CA_TIMEZONES: ReadonlySet<string> = new Set([
  'America/Toronto', 'America/Montreal', 'America/Vancouver', 'America/Edmonton',
  'America/Winnipeg', 'America/Halifax', 'America/St_Johns', 'America/Regina',
  'America/Moncton', 'America/Whitehorse', 'America/Dawson_Creek', 'America/Glace_Bay',
  'America/Goose_Bay', 'America/Iqaluit', 'America/Yellowknife', 'America/Nipigon',
]);

// IANA timezones that confidently indicate the US (opt-out) — lets US visitors be
// auto-granted via the fallback even when the geo cookie is missing.
const US_TIMEZONES: ReadonlySet<string> = new Set([
  'America/New_York', 'America/Detroit', 'America/Chicago', 'America/Denver',
  'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'America/Adak',
  'America/Boise', 'America/Indiana/Indianapolis', 'America/Kentucky/Louisville',
  'Pacific/Honolulu',
]);

/**
 * Best-effort client-side region from the browser timezone, used only when the
 * `bizrnr_geo` cookie is absent. Conservative: only EU (Europe/*), known CA zones,
 * and known US zones are classified; everything else is 'unknown' (→ denied).
 */
export function regionFromTimezone(): Region {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return 'unknown';
    if (tz.startsWith('Europe/')) return 'eu';
    if (CA_TIMEZONES.has(tz)) return 'ca';
    if (US_TIMEZONES.has(tz)) return 'us';
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

export interface ConsentDecision {
  /** analytics_storage state to apply via gtag consent update. */
  analytics: boolean;
  /** ad_storage / ad_user_data / ad_personalization state. */
  ads: boolean;
  /** Whether to show the consent banner (opt-in regions with no stored choice). */
  showBanner: boolean;
  /** Implied cookie value to persist; null = write nothing. Only ever an IMPLIED
   *  region default ('analytics_ads'); explicit choices resolve to null here. */
  persist: 'analytics' | 'analytics_ads' | null;
}

/**
 * Pure decision: given the stored choice and the visitor's region, what consent
 * state applies and whether to show the banner.
 *
 * COMPLIANCE INVARIANT (the #1641 regression guard): an EXPLICIT stored choice
 * ('granted' / 'denied' / 'ads_optout') ALWAYS wins and is NEVER overwritten —
 * `persist` is null for those, in every region. Region defaults apply only when
 * no choice is stored, OR to upgrade the LEGACY implied 'analytics' default to the
 * current region's ad default (implied → implied; an explicit choice is untouched).
 */
export function resolveConsentDecision({
  stored,
  region,
}: {
  stored: StoredConsent;
  region: Region;
}): ConsentDecision {
  // Explicit choices — immutable, no banner, no re-persist.
  if (stored === 'granted') return { analytics: true, ads: true, showBanner: false, persist: null };
  if (stored === 'denied') return { analytics: false, ads: false, showBanner: false, persist: null };
  if (stored === 'ads_optout') return { analytics: true, ads: false, showBanner: false, persist: null };
  // Implied opt-out default already in place — honor it.
  if (stored === 'analytics_ads') return { analytics: true, ads: true, showBanner: false, persist: null };
  // Legacy implied analytics-only default: upgrade to the ad default in opt-out
  // regions (implied → implied), else keep analytics-only.
  if (stored === 'analytics') {
    return adsGrantedByDefault(region)
      ? { analytics: true, ads: true, showBanner: false, persist: 'analytics_ads' }
      : { analytics: true, ads: false, showBanner: false, persist: null };
  }
  // No stored choice → region default.
  if (adsGrantedByDefault(region)) {
    // Opt-out region: imply analytics + ads so gtag, the MP relay (cookie-gated),
    // and the remarketing/Meta/TikTok pixels all flow without an Accept click.
    return { analytics: true, ads: true, showBanner: false, persist: 'analytics_ads' };
  }
  // Opt-in region (eu / ca / unknown): conservative deny + banner for an explicit choice.
  return { analytics: false, ads: false, showBanner: true, persist: null };
}

/** Map a stored cookie string into the strict StoredConsent union (else null). */
export function asStoredConsent(value: ConsentState | string | null | undefined): StoredConsent {
  return value === 'granted' ||
    value === 'denied' ||
    value === 'analytics' ||
    value === 'analytics_ads' ||
    value === 'ads_optout'
    ? value
    : null;
}
